import { DownloadOutlined } from '@ant-design/icons';
import { FormattedMessage, formatMessage, useIntl } from '@umijs/max';
import { type GetProp, message, notification, Upload, type UploadProps } from 'antd';
import { createStyles } from 'antd-style';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import ECB from 'crypto-js/mode-ecb';
import Pkcs7 from 'crypto-js/pad-pkcs7';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback } from 'react';
import type { LangType, SelectOptions } from '@/interface/common.interface';

dayjs.extend(utc);
dayjs.extend(timezone);
export const encrypt = (text: any, keyParams: any, ivParams = '') => {
  const key = keyParams;
  const iv = ivParams || '';

  return AES.encrypt(JSON.stringify(text), Utf8.parse(key), {
    iv: Utf8.parse(iv),
    mode: ECB,
    padding: Pkcs7, //正常服务器加密规则
  }).toString();
};

/**
 * AES 解密
 * @param ciphertext - 加密后的 Base64 字符串
 * @param keyParams - 密钥
 * @param ivParams - 初始向量（ECB 模式下不使用）
 * @returns 解密后的数据，如果解密失败返回原始数据
 */
export const decrypt = (ciphertext: string, keyParams: string, ivParams = '') => {
  const key = Utf8.parse(keyParams);
  const iv = Utf8.parse(ivParams);

  const decrypted = AES.decrypt(ciphertext, key, {
    iv,
    mode: ECB,
    padding: Pkcs7,
  });

  const decryptedStr = decrypted.toString(Utf8);
  try {
    return JSON.parse(decryptedStr);
  } catch {
    return decryptedStr;
  }
};

/**
 * 数据转为options
 * @param array 原始数组
 * @param label options 中的label 显示在下拉处
 * @param value value
 * @returns options for andt Select Component
 */
export const convertDataToSelectOptionsArray = <T,>(
  array: Array<any>,
  label: string,
  value: string,
): SelectOptions<T> => {
  if (!array.length) return [];

  return array.map((item) => ({
    label: item[label],
    value: item[value],
  }));
};

/**
 * a tag download trigger function
 * @param data original data for blob
 * @param fileName download file name shows name
 */
// export const aTagDownload = (data: Blob, fileName: string) => {
//   const url = window.URL.createObjectURL(new Blob([data]));
//   const a = document.createElement('a');
//   a.style.display = 'none';
//   a.href = url;
//   a.setAttribute('download', fileName);
//   document.body.appendChild(a);
//   a.click();
//   document.body.removeChild(a);
//   window.URL.revokeObjectURL(url);
// };

export const aTagDownload = (data: Blob, fileName: string) => {
  // 1. 自动校正后缀名：由于后端返回的是纯正的 Excel 压缩包(PK...)，必须用 .xlsx
  // 强行把可能传入的 .csv 替换为 .xlsx
  const correctedFileName = fileName.replace(/\.csv$/i, '.xlsx');

  // 2. 不要加 BOM，不要用 TextDecoder，直接使用后端给的 data 创建 URL
  // 必须确保 data 的 mime 类型为 excel 格式
  const excelBlob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(excelBlob);

  // 3. 触发标准的 a 标签下载
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.setAttribute('download', correctedFileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/**
 * 从url下载文件
 * @param fileUrl
 * @param fileName
 */
export const downloadFromUrl = (fileUrl: string, fileName: string) => {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = fileUrl;
  a.setAttribute('download', fileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * 有些接口数据量超过10万条时，需要展示报错而不是直接下载文件
 * @param data original data for blob
 * @param fileName download file name shows name
 */
export const limitDownload = async (data: Blob, fileName: string) => {
  try {
    const text = await data.text();
    const jsonData = JSON.parse(text);

    // 如果是JSON且code为9999，显示错误消息
    if (jsonData && jsonData.code === '9999') {
      message.error(jsonData.msg);
      return;
    }

    // 不是错误，重新创建blob进行下载
    const newBlob = new Blob([text]);
    aTagDownload(newBlob, fileName);
  } catch (_error) {
    // 不是JSON或解析失败，直接下载原始blob
    aTagDownload(data, fileName);
  }
};

export const insertToArrayList = (arr: Array<any>, index: number, ...newItems: any) => [
  // part of the array before the specified index
  ...arr.slice(0, index),
  // inserted items
  ...newItems,
  // part of the array after the specified index
  ...arr.slice(index),
];

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

export const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const formatDate = (odate: number) => {
  const timezone = localStorage.getItem('timezone');

  let tz = 'America/Sao_Paulo';
  if (timezone) {
    if (timezone === 'UTC+8:00') {
      tz = 'Asia/Shanghai';
    } else if (timezone === 'UTC-4:00') {
      tz = 'America/New_York';
    } else if (timezone === 'UTC-3:00') {
      tz = 'America/Sao_Paulo';
    }

    const dateInTimezone = dayjs.tz(odate, tz);

    // Format the date as needed, or return the moment object
    return dateInTimezone.format('YYYY-MM-DD HH:mm:ss');
  }
  return '';
};
/**
 *
 * @param time
 * @param type 0为北京时间
 * @returns
 */
export const formatTimeZone = (time: number, type: number) => {
  if (type === 0) {
    return dayjs.tz(time, 'Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss');
  }

  return formatDate(dayjs(time).add(12, 'hours').valueOf());
};

/**
 * 语言转化成 api接口参数
 * @param type
 * @returns
 */
export const langTypeTransfer = (type: LangType): string => {
  switch (type) {
    case 'zh_CN':
      return 'CMN';
    case 'en_US':
      return 'ENG';
    default:
      return '';
  }
};

/**
 * 封装useIntl，支持变量参数
 * @param id 国际化key
 * @param values 变量参数对象
 * @returns 格式化后的文本
 */
/**
 * 取翻译函数
 * @description 用 useCallback 固定引用，否则每次渲染都会返回新函数，
 *              让所有依赖 t 的 useMemo / useCallback 缓存失效
 */
export const useTranslation = () => {
  const intl = useIntl();
  return useCallback(
    (id: string, values?: Record<string, any>) => intl.formatMessage({ id }, values),
    [intl],
  );
};

// 根据特定属性升序排列数组中的对象的方法
function compare(property: string) {
  return (obj1: any, obj2: any) => {
    const value1 = obj1[property];
    const value2 = obj2[property];
    return value1 - value2; // 升序
  };
}

// 将平级navlist整理成树性结构
export const changeFlatToTree = (flat: any) => {
  const result = flat;
  let navlist = [];
  for (let i = 0; i < result.length; i++) {
    // this.navlist.push(result[i])
    if (result[i].parentId === 0) {
      navlist.push(result[i]);
    }
  }
  // 主菜单排序
  navlist = navlist.sort(compare('orderNum'));

  for (let k = 0; k < navlist.length; k++) {
    for (let i = 0; i < result.length; i++) {
      if (navlist[k].menuId === result[i].parentId) {
        navlist[k].children.push(result[i]);
      }
    }
    // 子菜单排序
    navlist[k].children.sort(compare('orderNum'));
  }
  //按钮排序
  for (let i = 0; i < navlist.length; i++) {
    for (let k = 0; k < navlist[i].children.length; k++) {
      for (let a = 0; a < result.length; a++) {
        if (navlist[i].children[k].menuId === result[a].parentId) {
          navlist[i].children[k].children.push(result[a]);
        }
      }
      navlist[i].children[k]?.children?.sort(compare('orderNum'));
    }
  }
  // 生成最终的菜单列表
  return navlist;
};

export const beforeUpload = (
  file: FileType,
  acceptImages: string[],
  size: number,
  errorSizeMsg: string,
  allowUpload: boolean = false,
) => {
  const isImage = acceptImages.includes(file.type.split('/')[1]);
  if (!isImage) {
    message.error(formatMessage({ id: 'common.onlyUpload' }, { types: acceptImages.join('/') }));
    return Upload.LIST_IGNORE;
  }
  if (file.size > size) {
    message.error(errorSizeMsg);
    return Upload.LIST_IGNORE;
  }
  return allowUpload; // 允许上传
};

export const addAllOptionToFieldOptions = (
  list: Array<any>,
  valueAll: any = '',
  labelAll: string = 'common.all',
) => {
  const all = { label: <FormattedMessage id={labelAll} />, value: valueAll };
  return [all, ...list];
};

// 通用的错误处理
export const handleError = (error: any) => {
  if (error instanceof Error) {
  } else {
  }
};

export const convertTreeToFlatMenus = (list: any) => {
  const tree: any = [];
  let temp: any;
  list?.forEach((el: any) => {
    if (el?.children) {
      const { children, ...other } = el;
      tree.push(other);
      temp = convertTreeToFlatMenus(children);
      tree.push(...temp);
    } else {
      tree.push(el);
    }
  });
  return tree;
};

export const convertFlatMenusToTree = (
  list: any[],
  parentId: string | null = null,
  leaf = 'routes',
): any[] => {
  // 兼容最外层的parentId不为null
  let currentParentId = parentId;
  if (currentParentId === null) {
    const allParentIds = list.map((item) => item.parentId);
    const allIds = list.map((item) => item.id);
    currentParentId = allParentIds.find((pid) => !allIds.includes(pid));
  }

  const tree: any[] = [];
  list?.forEach((el: any) => {
    if (el && el.parentId === currentParentId) {
      const obj: any = { ...el };
      const temp = convertFlatMenusToTree(list, el.id, leaf);
      if (temp.length) {
        obj[leaf] = temp;
      }
      tree.push(obj);
    }
  });

  return tree;
};

/**
 * 封装常用的样式
 */
export const useStyles = createStyles(() => {
  return {
    currencyTypeSegmented: {
      '.ant-segmented-group': {
        padding: '2px 3px',
        display: 'flex',
        flexWrap: 'wrap',
      },
    },
  };
});

/**
 * 图片比例转换
 * @param value 比例值
 * @param callback 回调函数
 * @returns 返回计算后的图片尺寸
 */
export const calculateImageSize = (
  value: string,
  callback: (width: number, height: number) => void,
) => {
  let newWidth = 0;
  let newHeight = 0;

  switch (value) {
    case '1*1':
      newWidth = 366;
      newHeight = 450;
      break;
    case '1*2':
      newWidth = 366;
      newHeight = 222;
      break;
    case '1*3':
      newWidth = 366;
      newHeight = 144;
      break;
    case '1*4':
      newWidth = 366;
      newHeight = 111;
      break;
    case '2*3':
      newWidth = 180;
      newHeight = 144;
      break;
    case '2*4':
      newWidth = 180;
      newHeight = 111;
      break;
    case '3*4':
      newWidth = 120;
      newHeight = 111;
      break;
    default:
      break;
  }
  callback(newWidth, newHeight);
};

export const openExportNotification = (navigate?: (path: string) => void, type = 'export') => {
  const actionText =
    type === 'import'
      ? formatMessage({
          id: 'reportManage.file.downloadImportResultPrompt',
        })
      : formatMessage({
          id: 'reportManage.file.downloadExportResultPrompt',
        });

  notification.open({
    message: null,
    description: (
      <div
        onClick={() => {
          if (navigate) {
            navigate('/reportManage/filemanagement');
            notification.destroy();
          }
        }}
      >
        <DownloadOutlined style={{ marginRight: '10px' }} />
        <span style={{ cursor: 'pointer' }}>{actionText}</span>
      </div>
    ),
    duration: null,
    placement: 'topRight',
  });
};
// 保留N位小数，默认2位
export const keepDecimals = (num: number, n = 2) => {
  const factor = 10 ** n;
  return Math.round(num * factor) / factor;
};

export const formatMinutes = (totalMinutes: number) => {
  const d = Math.floor(totalMinutes / (24 * 60)); // 总分钟除以一天的分钟数
  const h = Math.floor((totalMinutes % (24 * 60)) / 60); // 余下的分钟除以 60 得到小时
  const m = totalMinutes % 60; // 最后剩下的就是分钟

  return {
    days: d,
    hours: h,
    mins: m,
  };
};

/**
 * 无论域名怎么变，哪怕是 http/https 或带端口，都能精准提取
 */
export const getPath = (urlStr: string) => {
  try {
    // 只要 urlStr 是标准格式，new URL 就能自动剥离域名
    const urlObj = new URL(urlStr);
    return urlObj.pathname;
  } catch (e) {
    // 如果传入的不是合法 URL，返回原字符串或空
    return urlStr;
  }
};

/**
 * 移除手机号开头的加号
 * @param phone 原始手机号 示例："+63900412415"
 * @returns 处理后的手机号 示例："63900412415"
 */
export const formatPhoneWithoutPlus = (phone?: string): string => {
  if (!phone) return '';
  return phone.startsWith('+') ? phone.slice(1) : phone;
};
